// ─── PA (Plug & Abandonment) Workflow Models (M008) ──────────────────────────

// ─── Stage constants ─────────────────────────────────────────────────────────

/** Stage labels for the PA parent workflow (matches PaStage.cs on the API). */
export const PaStage = {
  Request:            'PE Workbook',
  LandReview:         'Land Review',
  SurfaceCost:        'Surface Cost Estimate',
  SiteReview:         'Survey/AFE',
  WorkOrder:          'Work Order Submission',
  CentralMaintenance: 'Site Excavation',
  DigPlan:            'Dig Plan',
  Scheduling:         'Pre Schedule',
  Execution:          'Job Execution',
  WellheadCutoff:     'Well Head Cut Off',
  JobCloseOut:        'Job Close Out',
  Cancelled:          'Cancelled',
  Resolved:           'Resolved',
} as const;

export type PaStageValue = (typeof PaStage)[keyof typeof PaStage];

/** Maps PaStage API values to header display labels used across all PA workspace headers. */
export const PA_STAGE_DISPLAY_LABEL: Record<string, string> = {
  [PaStage.Request]:            'PE Workbook',
  [PaStage.LandReview]:         'Land Review',
  [PaStage.SurfaceCost]:        'Surface Cost Estimate',
  [PaStage.SiteReview]:         'Survey / AFE Review',
  [PaStage.WorkOrder]:          'Work Order Submission',
  [PaStage.CentralMaintenance]: 'Site Excavation',
  [PaStage.DigPlan]:            'Dig Plan',
  [PaStage.Scheduling]:         'Scheduling',
  [PaStage.Execution]:          'Job Execution',
  [PaStage.WellheadCutoff]:     'Wellhead Cut-Off',
  [PaStage.JobCloseOut]:        'Job Close Out',
  [PaStage.Cancelled]:          'Cancelled',
  [PaStage.Resolved]:           'Resolved',
};

/** Ordered PA stage values matching the stage-bar tab order used across all PA workspace pages. */
export const PA_STAGE_ORDER: PaStageValue[] = [
  PaStage.Request,
  PaStage.LandReview,
  PaStage.SurfaceCost,
  PaStage.SiteReview,
  PaStage.WorkOrder,
  PaStage.CentralMaintenance,
  PaStage.DigPlan,
  PaStage.Scheduling,
  PaStage.Execution,
  PaStage.WellheadCutoff,
  PaStage.JobCloseOut,
];

/**
 * Resolves the stage-bar index for the case's actual persisted status, so the header progress
 * bar reflects where the case currently is — independent of which workspace page is being viewed
 * (e.g. viewing a completed Land Review page after the case has since advanced to a later stage).
 * Returns -1 when the status isn't one of the active-stage values (e.g. null while loading).
 */
export function getPaStageIndex(currentStatus: string | null | undefined): number {
  if (!currentStatus) return -1;
  return PA_STAGE_ORDER.indexOf(currentStatus as PaStageValue);
}

/** Stage labels for the WB (Wellbore) child workflow. */
export const WbStage = {
  CostEstimate:   'Subsurface Cost Estimate',
  Procedure:      'Subsurface Procedure',
  PermitApproval: 'Permit Approval',
  Cancelled:      'Cancelled',
  Resolved:       'Resolved',
} as const;

export type WbStageValue = (typeof WbStage)[keyof typeof WbStage];

/** Maps WbStage API values to header display labels used across all WB workspace headers. */
export const WB_STAGE_DISPLAY_LABEL: Record<string, string> = {
  [WbStage.CostEstimate]:   'Subsurface Cost Estimate',
  [WbStage.Procedure]:      'Subsurface Procedure',
  [WbStage.PermitApproval]: 'Permit Approval',
  [WbStage.Cancelled]:      'Cancelled',
  [WbStage.Resolved]:       'Resolved',
};

/** Ordered WB stage values matching the stage-bar tab order used across all WB workspace pages. */
export const WB_STAGE_ORDER: WbStageValue[] = [
  WbStage.CostEstimate,
  WbStage.Procedure,
  WbStage.PermitApproval,
];

/** Resolves the stage-bar index for the WB case's actual persisted status (see getPaStageIndex). */
export function getWbStageIndex(currentStatus: string | null | undefined): number {
  if (!currentStatus) return -1;
  return WB_STAGE_ORDER.indexOf(currentStatus as WbStageValue);
}

// ─── Audit Log ────────────────────────────────────────────────────────────────

/** PA-specific audit log entry. `displayDescription` is always populated by
 *  `PaCasesApiService` (mapped from `actionType`). */
export interface PaAuditLogEntry {
  id: number;
  timestamp: string;
  actionType: string;
  actorName: string;
  details: string;
  displayDescription: string;
  message?: string | null;
}

// ─── PA Case ─────────────────────────────────────────────────────────────────

export interface PaCaseDetail {
  id: number;
  caseId: number;
  caseNumber: string | null;
  wellName: string | null;
  api14: string | null;
  api12: string | null;
  api10: string | null;
  stateCode: string | null;
  fotId: string | null;
  costCenter: string | null;
  latitude: number | null;
  longitude: number | null;
  companyCode: string | null;
  localReferenceNumber: string | null;
  area: string | null;
  field: string | null;
  spudDate: string | null;
  completionDate: string | null;
  shutInDate: string | null;
  chevno: string | null;
  operator: string | null;
  countyName: string | null;
  plantCode: string | null;
  failureDate: string | null;
  isTimingZoneOne: boolean;
  isTimingZoneTwo: boolean;
  isDuneLizardNm: boolean;
  isDuneLizardTx: boolean;
  isHornshellMussel: boolean;
  isBlmLand: boolean;
  footageNorth: string | null;
  footageSouth: string | null;
  footageEast: string | null;
  footageWest: string | null;
  currentStatus: string | null;
  resolutionStatus: string | null;
  isCancelled: boolean;
  cancellationReason: string | null;
  cancellationComments: string | null;
  linkedWbCase: WbLinkedCase | null;
  linkedFdCase: FdLinkedCase | null;
  /** True when the REC (M010) spin-off was created from Wellhead Cutoff submit (FR-115). */
  isRecSpinoffCreated: boolean;
  /** Summary of the spun-off REC case for display/linking in the PA header (FR-115, GAP-REC-4). */
  linkedRecCase: RecLinkedCase | null;
  visibleTabs: string[];
  activeSurveySubprocess: string | null;
  leaseIdentificationNumber: string | null;
}

/** Minimal WB case data embedded in PaCaseDetail (returned from PA case endpoint). */
export interface WbLinkedCase {
  id: number;
  caseId: number;
  caseNumber: string | null;
  currentStatus: string | null;
  resolutionStatus: string | null;
  permitApprovalState: string | null;
}

/** Minimal FD spin-off data embedded in PaCaseDetail (returned from PA case endpoint). */
export interface FdLinkedCase {
  id: number;
  caseId: number;
  caseNumber: string | null;
  stage: string | null;
}

/** Minimal REC (M010) spin-off data embedded in PaCaseDetail (returned from PA case endpoint). */
export interface RecLinkedCase {
  id: number;
  caseId: number;
  caseNumber: string | null;
  caseStatus: string | null;
}

export interface PaStageParticipant {
  id: number;
  workflowCaseId: number;
  stageName: string;
  stepName: string | null;
  userId: string;
  userDisplayName: string | null;
  roleKey: string | null;
  completedOnUtc: string;
}

export interface PaCancellationRequest {
  cancellationReason: string;
  cancellationComments: string;
}

/** Snapshot of key Land Review fields embedded in WB case detail for read-only display. */
export interface PaLandReviewWioDto {
  id: number;
  participantId: string | null;
  participantName: string | null;
  typeOfInterest: string | null;
  decimalInterest: number | null;
  isOperator: boolean;
  sortOrder: number;
}

export interface PaLandReviewSnapshotDto {
  id: number;
  paCaseId: number;
  // §9.2.4.2 Land Information
  lwol: boolean;
  lwou: boolean;
  isSurfaceOwnerNotificationRequired: boolean | null;
  isLandNeedsReclaimed: boolean | null;
  isWioApprovalRequired: boolean | null;
  isAdditionalParties: boolean | null;
  copCriticalDate: string | null;
  workingInterestPercent: number | null;
  netRevenueInterestPercent: number | null;
  landComments: string | null;
  // §9.2.4.4.1 Lease Details
  leaseWellName: string | null;
  keyLandFileNo: string | null;
  otherLandFileNos: string | null;
  legalDescriptionOfLease: string | null;
  // §9.2.4.4.2 Property
  isChevronUsa: boolean;
  is100PercentIntProperty: boolean;
  isPooledUnit: boolean;
  isChevronMidcontinent: boolean;
  isMineralFee: boolean;
  isSurfaceFee: boolean;
  isLegacyNoble: boolean;
  isWiUnit: boolean;
  isPartialIntProperty: boolean;
  isSecRecUnit: boolean;
  // §9.2.4.4.3 Forfeiture
  gwi: number | null;
  forfeitureDecision: string | null;
  forfeitureIfNotWhy: string | null;
  forfeitureLeaseAcquiredDate: string | null;
  forfeitureLeaseholdCosts: string | null;
  forfeitureGrossNetAcres: string | null;
  forfeitureRightsDepthsForfeited: string | null;
  forfeitureTimeToRemoveCasing: string | null;
  forfeitureDaysToRestoreFromCessation: string | null;
  forfeitureSubstancesCovered: string | null;
  forfeitureSubstancesCoveredOtherDetail: string | null;
  forfeitureRightsDepthsRemaining: string | null;
  // §9.2.4.5 Ownership
  isNonOperatorApprovalRequired: boolean | null;
  percentageVoteRequired: number | null;
  wioTimeToReply: string | null;
  wioEntryMode: string | null;
  sectionNumber: string | null;
  qlsNumber: string | null;
  landOwnershipType: string | null;
  ownershipLanguage: string | null;
  isSua: boolean | null;
  suaProvisions: string | null;
  // §9.2.4.5.9 Salvage
  isSalvageAtSalvageValue: boolean | null;
  isSalvageChevron: boolean;
  isSalvageNonOperator: boolean;
  isSalvageLessorAssignor: boolean;
  isSalvageTractOwner: boolean;
  isSalvageSurfaceOwner: boolean;
  isSalvageOther: boolean;
  salvagePartyNames: string | null;
  salvageAsTo: string | null;
  salvageNameAddressNotes: string | null;
  // §9.2.4.5.10 Reassignment
  isReassignmentObligation: boolean | null;
  reassignmentToNameAddress: string | null;
  reassignmentDaysToReply: number | null;
  reassignmentDaysBeforeAbandonment: number | null;
  isReassignmentOffer: boolean;
  isReassignmentLease: boolean;
  isReassignmentOther: boolean;
  reassignmentOtherDetails: string | null;
  isReassignmentTender: boolean;
  isReassignmentAssignment: boolean;
  isReassignmentNotice: boolean;
  isReassignmentJoa: boolean;
  // §9.2.4.5.11 Comments / TC Limitations
  isAdverseLegalAction: boolean | null;
  adverseLegalActionDetails: string | null;
  isTitleClearanceLimitations: boolean | null;
  titleClearanceLimitationsDetails: string | null;
  // §9.2.4.6 Other
  isHabitatOfSandDunesLizardAndHornshellMussel: boolean | null;
  isConservationMeasuresRequired: boolean | null;
  surfaceOwnerName: string | null;
  surfaceOwnerAdditionalContact: string | null;
  surfaceOwnerLegalDescription: string | null;
  isSurfaceOwnerAccommodations: boolean | null;
  platDetails: string | null;
  otherNotes: string | null;
  // §9.2.4.5.2 WIO table
  workingInterestOwners: PaLandReviewWioDto[];
}

// ─── WB Case (full detail — used by WbCasesApiService) ───────────────────────

export interface WbCaseDetail {
  id: number;
  caseId: number;
  caseNumber: string | null;
  wellName: string | null;
  api14: string | null;
  fotId: string | null;
  currentStatus: string | null;
  resolutionStatus: string | null;
  paCaseId: number;
  paCaseNumber: string | null;
  /** Parent PA current status — used to resolve the back-link route segment. */
  paCurrentStatus: string | null;
  permitApprovalState: string | null;
  // Child Well Information (§9.2.1.2 / §9.2.2) — all read-only
  area: string | null;
  costCenter: string | null;
  stateCode: string | null;
  localReferenceNumber: string | null;
  plantCode: string | null;
  failureDate: string | null;
  companyCode: string | null;
  latitude: number | null;
  longitude: number | null;
  field: string | null;
  spudDate: string | null;
  completionDate: string | null;
  shutInDate: string | null;
  chevno: string | null;
  operator: string | null;
  countyName: string | null;
  footageNorth: string | null;
  footageSouth: string | null;
  footageEast: string | null;
  footageWest: string | null;
  visibleTabs: string[];
  /** True when the caller is permitted to invoke Edit Procedure (CH-S03-002, FR-157). */
  canEditProcedure: boolean;
  parentPaWorkbookData: PaRequestDetail | null;
  parentLandReviewData: PaLandReviewSnapshotDto | null;
}

// ─── Request stage ────────────────────────────────────────────────────────────

export interface PaFormationTopDto {
  id: number;
  formationName: string;
  topDepth: number;
  sortOrder: number;
}

export interface SavePaFormationTopDto {
  formationName: string;
  topDepth: number;
  sortOrder: number;
}

export interface PaWellHistoryEntryDto {
  id: number;
  eventDate: string | null;
  eventText: string;
  sortOrder: number;
}

export interface SavePaWellHistoryEntryDto {
  eventDate: string | null;
  eventText: string;
  sortOrder: number;
}

export interface PaContactDto {
  id: number;
  name: string;
  role: string | null;
  phoneNumber: string | null;
  email: string | null;
  sortOrder: number;
}

export interface SavePaContactDto {
  name: string;
  role: string | null;
  phoneNumber: string | null;
  email: string | null;
  sortOrder: number;
}

export interface PaRequestDetail {
  id: number;
  paCaseId: number;
  // §9.2.3.1 Well Header
  paPriorityLevel: string | null;
  currentWellStatus: string | null;
  isH2sOver100Ppm: boolean;
  isNormPresent: boolean;
  governingAuthority: string | null;
  survey: string | null;
  landSection: string | null;
  township: string | null;
  range: string | null;
  wbsNumber: string | null;
  groundElevation: string | null;       // Text per §9.2.3.1
  kellyBushingElevation: number | null; // Numeric per §9.2.3.1
  // §9.2.3.2 Well Plan
  objective: string | null;
  proposedZone: string | null;
  paTaDeadline: string | null;
  reasonForPaTa: string | null;
  lastProductionDate: string | null;
  lastProductionVolume: string | null;
  lastProductionPsi: string | null;
  currentPerforations: string | null;
  // §9.2.3.11 Facility
  hasFacilityAssociated: boolean;
  facilityName: string | null;
  facilityId: string | null;
  chevno: string | null;
  // Other
  isWaterboardNotificationRequired: boolean;
  isAttachWellHistory: boolean;
  peNotes: string | null;
  formationTopsRequestedOnUtc: string | null;
  formationTops: PaFormationTopDto[];
  wellHistoryEntries: PaWellHistoryEntryDto[];
  contacts: PaContactDto[];
}

export interface SavePaRequestRequest {
  // §9.2.1.1 Well Information (editable)
  wellName?: string | null;
  api14?: string | null;
  stateCode?: string | null;
  area?: string | null;
  fot?: string | null;
  costCenter?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  companyCode?: string | null;
  localReferenceNumber?: string | null;
  spudDate?: string | null;
  completionDate?: string | null;
  shutInDate?: string | null;
  // §9.2.3.1 Well Header
  paPriorityLevel?: string | null;
  currentWellStatus?: string | null;
  isH2sOver100Ppm?: boolean;
  isNormPresent?: boolean;
  governingAuthority?: string | null;
  survey?: string | null;
  landSection?: string | null;
  township?: string | null;
  range?: string | null;
  wbsNumber?: string | null;
  groundElevation?: string | null;
  kellyBushingElevation?: number | null;
  // §9.2.3.2 Well Plan
  objective?: string | null;
  proposedZone?: string | null;
  paTaDeadline?: string | null;
  reasonForPaTa?: string | null;
  lastProductionDate?: string | null;
  lastProductionVolume?: string | null;
  lastProductionPsi?: string | null;
  currentPerforations?: string | null;
  // §9.2.3.11 Facility
  hasFacilityAssociated?: boolean;
  facilityName?: string | null;
  facilityId?: string | null;
  chevno?: string | null;
  // Other
  isWaterboardNotificationRequired?: boolean;
  isAttachWellHistory?: boolean;
  peNotes?: string | null;
  // §9.2.1.1.1 Footage Call (distance to section lines)
  footageNorth?: string | null;
  footageSouth?: string | null;
  footageEast?: string | null;
  footageWest?: string | null;
  formationTops?: SavePaFormationTopDto[];
  wellHistoryEntries?: SavePaWellHistoryEntryDto[];
  contacts?: SavePaContactDto[];
}

// ─── Land Review stage ────────────────────────────────────────────────────────

export interface PaOperatorDto {
  id: number;
  operatorName: string;
  operatorType: string | null;
  contactName: string | null;
  address: string | null;
  phoneNumber: string | null;
  email: string | null;
  sortOrder: number;
}

export interface SavePaOperatorDto {
  operatorName: string;
  operatorType: string | null;
  contactName: string | null;
  address: string | null;
  phoneNumber: string | null;
  email: string | null;
  sortOrder: number;
}

export interface PaWorkingInterestOwnerDto {
  id: number;
  // §9.2.4.5.4 Manual table columns
  participantId: string | null;
  participantName: string | null;
  typeOfInterest: string | null;
  decimalInterest: number | null;
  isOperator: boolean;
  sortOrder: number;
}

export interface SavePaWorkingInterestOwnerDto {
  participantId: string | null;
  participantName: string | null;
  typeOfInterest: string | null;
  decimalInterest: number | null;
  isOperator: boolean;
  sortOrder: number;
}

export interface PaLandReviewDetail {
  id: number;
  paCaseId: number;

  // §9.2.4.2 Land Information
  lwol: boolean;
  lwou: boolean;
  isSurfaceOwnerNotificationRequired: boolean | null;
  isLandNeedsReclaimed: boolean | null;
  isWioApprovalRequired: boolean | null;
  workingInterestPercent: number | null;
  netRevenueInterestPercent: number | null;
  copCriticalDate: string | null;
  isAdditionalParties: boolean | null;
  landComments: string | null;

  // §9.2.4.4.1 Lease Details
  leaseWellName: string | null;
  keyLandFileNo: string | null;
  otherLandFileNos: string | null;
  legalDescriptionOfLease: string | null;

  // §9.2.4.4.2 Property
  isChevronUsa: boolean;
  is100PercentIntProperty: boolean;
  isPooledUnit: boolean;
  isChevronMidcontinent: boolean;
  isMineralFee: boolean;
  isSurfaceFee: boolean;
  isLegacyNoble: boolean;
  isWiUnit: boolean;
  isPartialIntProperty: boolean;
  isSecRecUnit: boolean;

  // §9.2.4.4.3 Forfeiture
  gwi: number | null;
  forfeitureDecision: string | null;
  forfeitureIfNotWhy: string | null;

  // §9.2.4.4.4 Forfeiture Follow-Up
  forfeitureLeaseAcquiredDate: string | null;
  forfeitureLeaseholdCosts: string | null;
  forfeitureGrossNetAcres: string | null;
  forfeitureRightsDepthsForfeited: string | null;
  forfeitureTimeToRemoveCasing: string | null;
  forfeitureDaysToRestoreFromCessation: string | null;
  forfeitureSubstancesCovered: string | null;
  forfeitureSubstancesCoveredOtherDetail: string | null;
  forfeitureRightsDepthsRemaining: string | null;

  // §9.2.4.5.1 Operators / WIO section
  isNonOperatorApprovalRequired: boolean | null;
  percentageVoteRequired: number | null;
  wioTimeToReply: string | null;
  wioEntryMode: string | null;

  // §9.2.4.5.6 Section
  sectionNumber: string | null;
  qlsNumber: string | null;

  // §9.2.4.5.7 / 5.8 Land Ownership / Language / SUA
  landOwnershipType: string | null;
  ownershipLanguage: string | null;
  isSua: boolean | null;
  suaProvisions: string | null;

  // §9.2.4.5.9 Salvage
  isSalvageAtSalvageValue: boolean | null;
  isSalvageChevron: boolean;
  isSalvageNonOperator: boolean;
  isSalvageLessorAssignor: boolean;
  isSalvageTractOwner: boolean;
  isSalvageSurfaceOwner: boolean;
  isSalvageOther: boolean;
  salvagePartyNames: string | null;
  salvageAsTo: string | null;
  salvageNameAddressNotes: string | null;

  // §9.2.4.5.10 Reassignment
  isReassignmentObligation: boolean | null;
  reassignmentToNameAddress: string | null;
  reassignmentDaysToReply: number | null;
  reassignmentDaysBeforeAbandonment: number | null;
  isReassignmentOffer: boolean;
  isReassignmentLease: boolean;
  isReassignmentOther: boolean;
  reassignmentOtherDetails: string | null;
  isReassignmentTender: boolean;
  isReassignmentAssignment: boolean;
  isReassignmentNotice: boolean;
  isReassignmentJoa: boolean;

  // §9.2.4.5.11 Comments / TC Limitations
  isAdverseLegalAction: boolean | null;
  adverseLegalActionDetails: string | null;
  isTitleClearanceLimitations: boolean | null;
  titleClearanceLimitationsDetails: string | null;

  // §9.2.4.6 Other tab
  isHabitatOfSandDunesLizardAndHornshellMussel: boolean | null;
  isConservationMeasuresRequired: boolean | null;
  surfaceOwnerName: string | null;
  surfaceOwnerAdditionalContact: string | null;
  surfaceOwnerLegalDescription: string | null;
  isSurfaceOwnerAccommodations: boolean | null;
  platDetails: string | null;
  otherNotes: string | null;

  // §9.2.4.7 Title Clearance
  isGeneratingTitleClearance: boolean;
  titleClearanceApprovalStatus: string | null;
  generateAttachTitleClearancePdf: boolean;
  titleClearanceApprovedBySupervisor: boolean | null;

  operators: PaOperatorDto[];
  workingInterestOwners: PaWorkingInterestOwnerDto[];

  // CH-S01-003: true when the case is at or past Work Order Submission; UI locks read-only sections
  isPostSiteReview: boolean;
}

export interface SavePaLandReviewRequest {
  // §10.3.2 Well Information editable fields
  wellName?: string | null;
  api14?: string | null;
  fotId?: string | null;
  area?: string | null;
  costCenter?: string | null;
  // §9.2.4.2 Land Information
  lwol?: boolean;
  isSurfaceOwnerNotificationRequired?: boolean | null;
  isLandNeedsReclaimed?: boolean | null;
  isWioApprovalRequired?: boolean | null;
  workingInterestPercent?: number | null;
  netRevenueInterestPercent?: number | null;
  copCriticalDate?: string | null;
  isAdditionalParties?: boolean | null;
  landComments?: string | null;
  // §9.2.4.4.1 Lease Details
  leaseWellName?: string | null;
  keyLandFileNo?: string | null;
  otherLandFileNos?: string | null;
  legalDescriptionOfLease?: string | null;
  // §9.2.4.4.2 Property
  isChevronUsa?: boolean;
  is100PercentIntProperty?: boolean;
  isPooledUnit?: boolean;
  isChevronMidcontinent?: boolean;
  isMineralFee?: boolean;
  isSurfaceFee?: boolean;
  isLegacyNoble?: boolean;
  isWiUnit?: boolean;
  isPartialIntProperty?: boolean;
  isSecRecUnit?: boolean;
  // §9.2.4.4.3 Forfeiture
  gwi?: number | null;
  forfeitureDecision?: string | null;
  forfeitureIfNotWhy?: string | null;
  // §9.2.4.4.4 Forfeiture Follow-Up
  forfeitureLeaseAcquiredDate?: string | null;
  forfeitureLeaseholdCosts?: string | null;
  forfeitureGrossNetAcres?: string | null;
  forfeitureRightsDepthsForfeited?: string | null;
  forfeitureTimeToRemoveCasing?: string | null;
  forfeitureDaysToRestoreFromCessation?: string | null;
  forfeitureSubstancesCovered?: string | null;
  forfeitureSubstancesCoveredOtherDetail?: string | null;
  forfeitureRightsDepthsRemaining?: string | null;
  // §9.2.4.5.1 Operators / WIO section
  isNonOperatorApprovalRequired?: boolean | null;
  percentageVoteRequired?: number | null;
  wioTimeToReply?: string | null;
  wioEntryMode?: string | null;
  // §9.2.4.5.6 Section
  sectionNumber?: string | null;
  qlsNumber?: string | null;
  // §9.2.4.5.7 / 5.8
  landOwnershipType?: string | null;
  ownershipLanguage?: string | null;
  isSua?: boolean | null;
  suaProvisions?: string | null;
  // §9.2.4.5.9 Salvage
  isSalvageAtSalvageValue?: boolean | null;
  isSalvageChevron?: boolean;
  isSalvageNonOperator?: boolean;
  isSalvageLessorAssignor?: boolean;
  isSalvageTractOwner?: boolean;
  isSalvageSurfaceOwner?: boolean;
  isSalvageOther?: boolean;
  salvagePartyNames?: string | null;
  salvageAsTo?: string | null;
  salvageNameAddressNotes?: string | null;
  // §9.2.4.5.10 Reassignment
  isReassignmentObligation?: boolean | null;
  reassignmentToNameAddress?: string | null;
  reassignmentDaysToReply?: number | null;
  reassignmentDaysBeforeAbandonment?: number | null;
  isReassignmentOffer?: boolean;
  isReassignmentLease?: boolean;
  isReassignmentOther?: boolean;
  reassignmentOtherDetails?: string | null;
  isReassignmentTender?: boolean;
  isReassignmentAssignment?: boolean;
  isReassignmentNotice?: boolean;
  isReassignmentJoa?: boolean;
  // §9.2.4.5.11 Comments / TC Limitations
  isAdverseLegalAction?: boolean | null;
  adverseLegalActionDetails?: string | null;
  isTitleClearanceLimitations?: boolean | null;
  titleClearanceLimitationsDetails?: string | null;
  // §9.2.4.6 Other tab
  isHabitatOfSandDunesLizardAndHornshellMussel?: boolean | null;
  isConservationMeasuresRequired?: boolean | null;
  surfaceOwnerName?: string | null;
  surfaceOwnerAdditionalContact?: string | null;
  surfaceOwnerLegalDescription?: string | null;
  isSurfaceOwnerAccommodations?: boolean | null;
  platDetails?: string | null;
  otherNotes?: string | null;
  // §9.2.4.7
  isGeneratingTitleClearance?: boolean;
  titleClearanceApprovalStatus?: string | null;
  generateAttachTitleClearancePdf?: boolean;
  titleClearanceApprovedBySupervisor?: boolean | null;
  operators?: SavePaOperatorDto[];
  workingInterestOwners?: SavePaWorkingInterestOwnerDto[];
}

// ─── Surface Cost Estimate stage ─────────────────────────────────────────────
// Matches backend PaSurfaceCostEstimateDto (Phase 3 — task 3.2.1)

export interface PaSurfaceCostEstimateDetail {
  id: number;
  paCaseId: number;
  // AES estimate
  aesTotalBeforeGa: number | null;
  aesGaPercent: number | null;       // sourced from Callfield 'G&A Percent'
  aesGaCost: number | null;          // server-computed: aesTotalBeforeGa * aesGaPercent / 100
  aesTotalAfterGa: number | null;    // server-computed: aesTotalBeforeGa + aesGaCost
  aesSummaryOfWork: string | null;   // optional, max 512 chars
  // AEF estimate (optional section)
  aefTotalBeforeGa: number | null;
  aefGaPercent: number | null;
  aefGaCost: number | null;
  aefTotalAfterGa: number | null;
  aefSummaryOfWork: string | null;
}

export interface SavePaSurfaceCostRequest {
  // §9.2.1.2 Well Information editable fields
  wellName?: string | null;
  api14?: string | null;
  fotId?: string | null;
  area?: string | null;
  costCenter?: string | null;
  // Estimate fields
  aesTotalBeforeGa?: number | null;   // required on submit (FR-068)
  aesSummaryOfWork?: string | null;
  aefTotalBeforeGa?: number | null;   // optional (FR-069)
  aefSummaryOfWork?: string | null;
}

// ─── Site Review stage ────────────────────────────────────────────────────────
// Matches backend PaSurfaceSurveyDto / PaAfeReviewDto (Phase 3 — task 3.2.1)

export interface PaSurfaceSurveyDetail {
  id: number;
  // Read-only projected from PaCase / PaRequest (§9.2.6.2.10)
  latitude: number | null;
  longitude: number | null;
  landSection: string | null;
  township: string | null;
  range: string | null;
  // §9.2.6.2.1 — powerline (X from Callfield 'Powerline Distance')
  isPowerlineWithinThreshold: boolean | null;
  // §9.2.6.2.2 — Project Information
  workBoundariesStacked: boolean | null;   // boolean dropdown Yes/No (§9.2.6.2.11)
  surveyField: string | null;
  elevation: string | null;
  leaseIdentificationNumber: string | null;
  siteEvaluatorName: string | null;
  siteEvaluationDate: string | null;  // DateOnly — ISO date string 'YYYY-MM-DD'
  anchorTestDate: string | null;       // DateOnly — cannot be future (FR-075)
  // §9.2.6.2.3 — LOC Information
  isElectrical: boolean | null;
  isUnitOnLoc: boolean | null;
  isSkidOnLoc: boolean | null;
  isConcreteOnLoc: boolean | null;
  // §9.2.6.2.4 — Well Information
  isRodsVisible: boolean | null;
  isSlimhole: boolean | null;
  isFlowlineRemoval: boolean | null;
  flowlineRemovalDetail: string | null;   // visible when isFlowlineRemoval
  // §9.2.6.2.5 — CBO Information
  isCboSign: boolean | null;
  isStainingOfGround: boolean | null;
  isHolesByWell: boolean | null;
  isWellTemp: boolean | null;
  wellTempDetail: string | null;          // visible when isWellTemp
  // §9.2.6.2.6 — Wellhead/Size/Rating Info
  wellheadSizeRatingInfo: string | null;
  pressure: string | null;
  isCorrosion: boolean | null;
  isApiWellhead: boolean | null;
  isSlipType: boolean | null;
  isThreadsShowing: boolean | null;
  isNonApiWellhead: boolean | null;
  isLeak: boolean | null;
  isDonutRams: boolean | null;
  isBullPlugged: boolean | null;
  isBolts: boolean | null;
  conditionComments: string | null;
  // §9.2.6.2.8 — Location Needs
  isBackhoePrepCrew: boolean | null;
  isWellheadPrepCrew: boolean | null;
  isNeedsRig: boolean | null;
  isNeedsCoil: boolean | null;
  // §9.2.6.2.9 — Additional Text Areas
  locationPrepComments: string | null;
  projectDescriptionDrivingDirections: string | null;
  supportingPhotos: string | null;         // rich text editor — copied verbatim to PDF
  /** True when the Surface Survey has been submitted; form is read-only for all roles once set. */
  isSubmitted: boolean;
}

export interface SavePaSurfaceSurveyRequest {
  // §9.2.1.2 Well Information editable fields (shared non-submission pattern)
  wellName?: string | null;
  api14?: string | null;
  fotId?: string | null;
  area?: string | null;
  costCenter?: string | null;
  isPowerlineWithinThreshold?: boolean | null;
  workBoundariesStacked?: boolean | null;
  surveyField?: string | null;
  elevation?: string | null;
  leaseIdentificationNumber?: string | null;
  siteEvaluatorName?: string | null;
  siteEvaluationDate?: string | null;   // required on submit (FR-075)
  anchorTestDate?: string | null;        // optional; cannot be future
  isElectrical?: boolean | null;
  isUnitOnLoc?: boolean | null;
  isSkidOnLoc?: boolean | null;
  isConcreteOnLoc?: boolean | null;
  isRodsVisible?: boolean | null;
  isSlimhole?: boolean | null;
  isFlowlineRemoval?: boolean | null;
  flowlineRemovalDetail?: string | null;
  isCboSign?: boolean | null;
  isStainingOfGround?: boolean | null;
  isHolesByWell?: boolean | null;
  isWellTemp?: boolean | null;
  wellTempDetail?: string | null;
  wellheadSizeRatingInfo?: string | null;
  pressure?: string | null;
  isCorrosion?: boolean | null;
  isApiWellhead?: boolean | null;
  isSlipType?: boolean | null;
  isThreadsShowing?: boolean | null;
  isNonApiWellhead?: boolean | null;
  isLeak?: boolean | null;
  isDonutRams?: boolean | null;
  isBullPlugged?: boolean | null;
  isBolts?: boolean | null;
  conditionComments?: string | null;
  isBackhoePrepCrew?: boolean | null;
  isWellheadPrepCrew?: boolean | null;
  isNeedsRig?: boolean | null;
  isNeedsCoil?: boolean | null;
  locationPrepComments?: string | null;
  projectDescriptionDrivingDirections?: string | null;
  supportingPhotos?: string | null;
}

// ─── WBS entries (shared between AFE Review response and save request) ─────────

export interface PaWbsEntryDto {
  id: number;
  wbsNumber: string;
  allocation: number | null;   // % decimal
  sortOrder: number;
}

export interface SavePaWbsEntryRequest {
  wbsNumber: string;
  allocation?: number | null;
  sortOrder: number;
}

// ─── AFE Review ───────────────────────────────────────────────────────────────
// Matches backend PaAfeReviewDto (Phase 3 — task 3.2.1)

export interface PaAfeReviewDetail {
  id: number;
  // Read-only mapped from PaSurfaceCostEstimate (FR-078)
  aesTotalBeforeGa: number | null;
  aesTotalAfterGa: number | null;
  aefTotalBeforeGa: number | null;
  aefTotalAfterGa: number | null;
  // Read-only mapped from WB child case (FR-078)
  abnCostEstimate: number | null;
  // Editable
  companyCode: string | null;
  afeLink: string | null;             // must start with approved prefix (FR-079)
  approvedAes: number | null;
  approvedAef: number | null;
  approvedAbn: number | null;
  wbsComments: string | null;
  hasApprovedWbsAttached: boolean;
  isApprovedTitleClearanceAttached: boolean;
  isFdSpinoffCreated: boolean;
  /** True when an FD spin-off already exists from the Request stage. Locks Facility Review fields per FR-080. */
  isFdAlreadyCreated: boolean;
  wbsEntries: PaWbsEntryDto[];
  // §9.2.7.3 Facility Review (locked once isFdSpinoffCreated)
  createFacilityDecom: boolean | null;
  facilityName: string | null;
  facilityId: string | null;
  facilityChevno: string | null;
  /** True when the AFE Review has been submitted; form is read-only for all roles once set. */
  isSubmitted: boolean;
}

export interface SavePaAfeReviewRequest {
  // §9.2.1.2 Well Information editable fields
  wellName?: string | null;
  api14?: string | null;
  fotId?: string | null;
  area?: string | null;
  costCenter?: string | null;
  companyCode?: string | null;
  afeLink?: string | null;
  approvedAes?: number | null;
  approvedAef?: number | null;
  approvedAbn?: number | null;
  wbsComments?: string | null;
  hasApprovedWbsAttached?: boolean;
  isApprovedTitleClearanceAttached?: boolean;
  wbsEntries?: SavePaWbsEntryRequest[];
  createFacilityDecom?: boolean | null;
  facilityName?: string | null;
  facilityId?: string | null;
  facilityChevno?: string | null;
}

// ─── Work Order Submission stage ──────────────────────────────────────────────
// Matches backend PaWorkOrderSubmissionDto / SavePaWorkOrderDto (Phase 3 — task 3.2.1)

export interface PaWorkOrderDetail {
  id: number;
  paCaseId: number;
  areWorkOrdersSubmitted: boolean;   // single required checkbox (§9.2.8)
  additionalComments: string | null; // optional text area
}

export interface SavePaWorkOrderRequest {
  areWorkOrdersSubmitted: boolean;
  additionalComments?: string | null;
}

// §9.2.1.2 Well Information editable fields for Work Order stage (mirrors central-maintenance pattern)
export interface SavePaWorkOrderWellInfoRequest {
  wellName?: string | null;
  api14?: string | null;
  fotId?: string | null;
  area?: string | null;
  costCenter?: string | null;
}

// ─── Central Maintenance stage ────────────────────────────────────────────────
// Matches backend PaCentralMaintenanceDto / EamWorkOrderDto (Phase 3 — task 3.2.1)

export interface EamActivityDto {
  comment: string | null;       // §9.2.9.3 Activity Field: comments
  commentDate: string | null;   // ISO datetime
  commentBy: string | null;
}

export interface EamWorkOrderDto {
  // §9.2.9.2 WO table columns
  workOrderNumber: string | null;
  description: string | null;
  woStatusCode: string | null;
  createdDateTime: string | null;  // ISO datetime; sort desc (FR-084)
  scheduledDate: string | null;    // ISO date 'YYYY-MM-DD'
  completedDate: string | null;    // ISO datetime
  assignee: string | null;
  // §9.2.9.4 submit-modal extra columns
  updatedDate: string | null;
  updatedBy: string | null;
  woComments: string | null;
  priorityCode: string | null;
  // §9.2.9.3 expanded activity rows (sorted by date desc)
  activities: EamActivityDto[];
}

export interface PaCentralMaintenanceDetail {
  id: number;
  isActiveWorkOrderAcknowledged: boolean;
  lastPolledOnUtc: string | null;           // ISO datetime
  hasWorkOrdersCompletedEmailSent: boolean;
  /** True when any WO has WoStatusCode != 'COMP' (BRU-064). Used by frontend to gate submit. */
  hasActiveWorkOrders: boolean;
  workOrders: EamWorkOrderDto[];
}

export interface SavePaCentralMaintenanceWellInfoRequest {
  // §9.2.1.2 / §9.2.9.1 Well Information editable fields
  wellName?: string | null;
  api14?: string | null;
  fotId?: string | null;
  area?: string | null;
  costCenter?: string | null;
}

// ─── Dig Plan stage ───────────────────────────────────────────────────────────

export interface PaBusinessPartnerEmailDto {
  id: number;
  email: string;
  sortOrder: number;
}

export interface PaDigPlanAttachmentInfo {
  id: number;
  fileName: string;
  uploadedBy: string | null;
  uploadedDate: string; // ISO datetime
}

export interface PaDigPlanDetail {
  id: number;
  /** Current 3-step sub-step: 'creation' | 'review' | 'execution' */
  currentStep: string | null;
  isCreationComplete: boolean;
  isReviewComplete: boolean;
  isExecutionComplete: boolean;
  isWellPrepComplete: boolean;
  approvalComments: string | null;
  /** Rejection comments returned from the previous review step (read-only display in Creation). */
  rejectionComments: string | null;
  businessPartnerEmails: PaBusinessPartnerEmailDto[];
  /** Most-recent DigPlan attachment — null when none uploaded yet (§9.2.10.2). */
  digPlanAttachment: PaDigPlanAttachmentInfo | null;
}

export interface SavePaDigPlanRequest {
  isWellPrepComplete?: boolean;
  approvalComments?: string | null;
  rejectionComments?: string | null;
  /** Array of email strings; server replaces existing rows. */
  businessPartnerEmails?: string[];
}

// §9.2.1.2 Well Information editable fields for Dig Plan stage
export interface SavePaDigPlanWellInfoRequest {
  wellName?: string | null;
  api14?: string | null;
  fotId?: string | null;
  area?: string | null;
  costCenter?: string | null;
}

// ─── Scheduling stage ─────────────────────────────────────────────────────────

export interface PaSchedulingDetail {
  id: number;
  paCaseId: number;
  rigId: string | null;
  queueOrder: number | null;
  projectedJobDays: number | null;
  isWellviewJobEntered: boolean;
  wellviewJobType: string | null;
  wellviewJobCreationDate: string | null; // 'YYYY-MM-DD'
  isConservationDocumentGenerated: boolean;
  /** true when both WB child resolved AND wellview fields complete (dual gate FR-093) */
  canEditScheduling: boolean;
  /** PA/TA Deadline from parent Request's Well Plan (§9.2.11). 'YYYY-MM-DD' or null. */
  paTaDeadline: string | null;
  /** SS Cost Type from child WB Subsurface Cost Estimate (§9.2.11). */
  ssCostType: string | null;
  /**
   * True when the current date is within the configured conservation date window (§6.1.2).
   * Always true for non-NM states. Frontend uses this to gate the Conservation Document section.
   */
  withinConservationDates: boolean;
}

export interface SavePaSchedulingRequest {
  rigId?: string | null;
  queueOrder?: number | null;
  projectedJobDays?: number | null;
}

// §9.2.1.2 Well Information editable fields for Scheduling stage
export interface SavePaSchedulingWellInfoRequest {
  wellName?: string | null;
  api14?: string | null;
  fotId?: string | null;
  area?: string | null;
  costCenter?: string | null;
}

/** One row in the SS Scheduling rig-queue drag-and-drop table (§9.2.11). */
export interface PaRigQueueRow {
  /** Positive = PA case ID; negative = RL case ID (sign-negated to avoid collision). */
  paCaseId: number;
  caseNumber: string;
  wellName: string;
  queueOrder: number;
  /** PA/TA Deadline from parent Request. 'YYYY-MM-DD' or null. Null for non-PA rows. */
  paTaDeadline: string | null;
  /** SS Cost Type from child WB Subsurface Cost Estimate. Null for non-PA rows. */
  ssCostType: string | null;
  /** "PA" or "RL". PA rows are draggable; RL rows are read-only display entries. */
  caseType: string;
}

/** Item sent in the batch queue-order update payload (§9.2.11). */
export interface UpdatePaRigQueueItem {
  paCaseId: number;
  queueOrder: number;
}

export interface SavePaWellviewRequest {
  isWellviewJobEntered: boolean;
  wellviewJobType?: string | null;
  wellviewJobCreationDate?: string | null; // 'YYYY-MM-DD'
}

// ─── Execution stage ─────────────────────────────────────────────────────────

export interface PaExecutionDetail {
  id: number;
  paCaseId: number;
  isPowerlineWithinThreshold: boolean | null;
  isPowerlineVarianceNeeded: boolean;
  powerlineVarianceApprovalRequestor: string | null;
  powerlineVarianceApprovalStatus: string | null; // 'Approved' | 'Rejected' | null
  powerlineVarianceApprovalComments: string | null;
  isRetrievalRequired: boolean;
  retrievalPartner: string | null;
  pluggingDate: string | null; // 'YYYY-MM-DD'
  retrievalPartnerEmails: string[];
  /** True when the caller must not keep tubing material (KeepingTubingMaterial = false = retrieval needed). */
  keepingTubingMaterial: boolean;
  /** Server-computed two-condition gate: true when PluggingDate is required (CH-S03-005, FR-107). */
  pluggingDateRequired: boolean;
  /** Server-computed gate: true when Rod/Tubing Retrieval attachment is required (CH-S03-005, FR-105). */
  rodTubingAttachmentRequired: boolean;
  /**
   * True when today is within the Admin M012 configured conservation date window (FR-099).
   * For non-NM wells always true. Used together with conservation polygon flags on PaCaseDetail
   * to control Download Conservation Measures Template button visibility.
   */
  withinConservationDates: boolean;
}

export interface SavePaExecutionRequest {
  isPowerlineVarianceNeeded?: boolean;
  powerlineVarianceApprovalRequestor?: string | null;
  isRetrievalRequired?: boolean;
  retrievalPartner?: string | null;
  pluggingDate?: string | null;
  retrievalPartnerEmails?: string[];
}

export interface SavePaExecutionWellInfoRequest {
  wellName?: string | null;
  api14?: string | null;
  fotId?: string | null;
  area?: string | null;
  costCenter?: string | null;
}

// ─── Wellhead Cutoff stage ────────────────────────────────────────────────────

export interface SavePaWellheadCutoffWellInfoRequest {
  wellName?: string | null;
  api14?: string | null;
  fotId?: string | null;
  area?: string | null;
  costCenter?: string | null;
}

export interface PaWellheadCutoffDetail {
  id: number;
  paCaseId: number;
  noteToCoordinator: string | null;
  noteToReclamationTeam: string | null;
  sendBackReason: string | null;
  isRecSpinoffCreated: boolean;
  /** Read-only Well Information projection from PaCase (5.1.S03-04, FR-111). */
  wellInformation: PaWellheadCutoffWellInfo | null;
}

export interface PaWellheadCutoffWellInfo {
  wellName: string | null;
  api14: string | null;
  fotId: string | null;
  area: string | null;
  costCenter: string | null;
  stateCode: string | null;
  companyCode: string | null;
}

export interface SavePaWellheadCutoffRequest {
  noteToCoordinator?: string | null;
  noteToReclamationTeam?: string | null;
}

export interface PaWellheadCutoffSendBackRequest {
  reason: string;
}

// ─── Job Close Out stage ──────────────────────────────────────────────────────

export interface PaJobCloseOutDetail {
  id: number;
  paCaseId: number;
  isAgencyNotified: boolean;
  agencyNotifiedDate: string | null; // 'YYYY-MM-DD'
  isClosureApproved: boolean;
  agencyApprovalDate: string | null; // 'YYYY-MM-DD'
  winRecipients: string[];
}

export interface SavePaJobCloseOutRequest {
  isAgencyNotified?: boolean;
  agencyNotifiedDate?: string | null;
  isClosureApproved?: boolean;
  agencyApprovalDate?: string | null;
  winRecipients?: string[];
}

// ─── WB Subsurface Cost Estimate ─────────────────────────────────────────────

export interface WbSubsurfaceCostEstimateDetail {
  id: number;
  wbCaseId: number;
  /** Callfield-sourced: 'Rig Cost' | 'Coil Cost' | 'Rig + Coil Cost' | 'Other'. Default = 'Rig Cost'. */
  costEstimateType: string | null;
  // ABN section
  abnDays: number | null;
  abnCostPerDay: number | null;
  abnContingency: number | null;
  /** Server-computed: abnDays × abnCostPerDay */
  abnTotalDaysCost: number | null;
  /** Server-computed: abnTotalDaysCost + abnContingency */
  abnCost: number | null;
  abnSummaryOfWork: string | null;    // max 512 chars
  // ABP section
  abpCostEstimate: number | null;
  abpSummaryOfWork: string | null;    // max 512 chars
}

export interface SaveWbCostEstimateRequest {
  costEstimateType?: string | null;
  abnDays?: number | null;
  abnCostPerDay?: number | null;
  abnContingency?: number | null;
  abnSummaryOfWork?: string | null;
  abpCostEstimate?: number | null;
  abpSummaryOfWork?: string | null;
}

// ─── WB Short Procedure ───────────────────────────────────────────────────────

export interface ProcedureTemplateOption {
  id: number;
  name: string;
  content: string | null;
}

export interface WbSubsurfaceProcedureDetail {
  id: number;
  wbCaseId: number;
  procedureTemplateId: number | null;
  procedureDetails: string | null;
  hasPermitBeenRequested: boolean;
  permitExpirationDate: string | null;  // ISO date string 'YYYY-MM-DD'
  /** True when Back was clicked — procedure is no longer required (FR-130). */
  isRequirednessSuspended: boolean;
  formationTops: PaFormationTopDto[];
  tubularsRodsAttachmentName: string | null;
  /** File name of the Current Wellbore Diagram PDF attachment (§9.2.17.4 / FR-129). */
  currentWellboreDiagramPdfName: string | null;
  /** File name of the Proposed Wellbore Diagram PDF attachment (§9.2.17.4 / FR-129). */
  proposedWellboreDiagramName: string | null;
  /** File name of the Permit attachment (§9.2.17.4 / FR-129). */
  permitAttachmentName: string | null;
  /** File name of the P&A Procedure attachment required at this stage (S04-FB-001). */
  paProcedureAttachmentName: string | null;
}

export interface SaveWbProcedureRequest {
  procedureTemplateId?: number | null;
  procedureDetails?: string | null;
  permitExpirationDate?: string | null;
  hasPermitBeenRequested?: boolean;
}

// ─── WB Permit Approval ───────────────────────────────────────────────────────

export interface WbPermitApprovalDetail {
  id: number;
  wbCaseId: number;
  isTeamLeadApproved: boolean;
  teamLeadComments: string | null;
  teamLeadApprovedOnUtc: string | null;     // ISO datetime
  isSuperintendentApproved: boolean;
  superintendentComments: string | null;
  superintendentApprovedOnUtc: string | null;
  permitExpirationDate: string | null;      // ISO date
  /** Title Clearance Expiration Date (CH-S05-001, FR-163) — distinct from the subsurface PermitExpirationDate. */
  titleClearanceExpirationDate: string | null;      // ISO date
  /** Short Procedure RTE content from WB Procedure stage (§9.2.18.3 read-only). */
  procedureDetails: string | null;
  /** Formation Tops from parent PA case (§9.2.18.2 read-only). */
  formationTops: PaFormationTopDto[];
  /** Tubulars & Rods attachment — status maintained, action hidden (§9.2.18.4). */
  tubularsRodsAttachmentName: string | null;
  /** Current Wellbore Diagram attachment — status maintained, action hidden (§9.2.18.4). */
  currentWellboreDiagramPdfName: string | null;
  /** Proposed Wellbore Diagram attachment — status maintained, action hidden (§9.2.18.4). */
  proposedWellboreDiagramName: string | null;
  /** Subsurface Permit attachment — editable, required on submit (§9.2.18.4). */
  permitAttachmentName: string | null;
  /** P&A Procedure attachment — read-only status display (§9.2.18.3). */
  paProcedureAttachmentName: string | null;
}

export interface SaveWbPermitApprovalRequest {
  isTeamLeadApproved?: boolean | null;
  teamLeadComments?: string | null;
  isSuperintendentApproved?: boolean | null;
  superintendentComments?: string | null;
  permitExpirationDate?: string | null;
  titleClearanceExpirationDate?: string | null;
}

// -- Comments (FR-018, FR-019) ---------------------------------------------
export interface PaCommentDto {
  id: number;
  body: string;
  authorDisplayName: string | null;
  createdOnUtc: string;
}

export interface AddPaCommentRequest {
  body: string;
}

// ─── Stage-queue models (6.2B) ────────────────────────────────────────────────

/** Minimal PA case row for dashboard stage-queue tabs (GET /api/pa-cases/stage-queue). */
export interface PaStageQueueItem {
  id: number;
  caseNumber: string;
  wellName: string | null;
  api14: string | null;
  fot: string | null;
  /** PA case Area snapshotted from MasterWellList; optional for backward compatibility with older callers. */
  area?: string | null;
  currentStatus: string | null;
  daysInCurrentStep: number;
  /** Wellview job entered flag from PaSchedulingData; null when no scheduling record exists yet (FR-094). */
  wellviewJobEntered: boolean | null;
  /** Wellview job type; null when not yet entered (FR-094 / US-008). */
  wellviewJobType: string | null;
  /** Wellview job creation date ISO string; null when not yet entered (FR-094 / US-008). */
  wellviewJobCreationDate: string | null;
  /** True when any conservation flag is active for this PA case (S04-FB-003). */
  conservationRequired: boolean;
  /** Net revenue interest percentage — PaLandReview value if saved, otherwise MasterWellList. */
  netRevenueInterestPercent?: string | null;
  /** Working interest percentage — PaLandReview value if saved, otherwise MasterWellList. */
  workingInterestPercent?: string | null;
}

/** Paginated wrapper for GET /api/pa-cases/stage-queue-paged, used by the Submissions dashboard's Plug & Abandonment tab. */
export interface PaStageQueuePage {
  totalCount: number;
  items: PaStageQueueItem[];
}

/** PA case row for the Land Dashboard Title Clearance Completion tab (GET /api/pa-cases/title-clearance-queue). */
export interface PaTitleClearanceQueueItem {
  id: number;
  caseNumber: string;
  wellName: string | null;
  api14: string | null;
  fot: string | null;
  sectionName: string | null;
  /** Display name of the user who last saved/submitted the Land Review stage for this case. */
  landRep: string | null;
  /** Server-computed: "Not Attached" or "In Review". */
  approvalStatus: string;
  /** The current PA case status (FR-158). */
  caseStatus: string | null;
}

/** Minimal WB case row for Planning dashboard P&A Subsurface Procedure tab (GET /api/wb-cases/stage-queue). */
export interface WbStageQueueItem {
  id: number;
  caseNumber: string;
  wellName: string | null;
  api14: string | null;
  fot: string | null;
  currentStatus: string | null;
  daysInCurrentStep: number;
}